/* eslint-disable max-lines-per-function */
import './PROD.scss';
import ProductBase from 'pages/PROD/ProductBase';

export default class PROD extends ProductBase {

	onReady () {
		// Initialize Main-Image, Thumbnails, & PhotoSwipe Close-up Gallery
		this.initImageGallery();
		this.initPurchaseForm();
		this.initStickyHeader();
		this.removeCloseupBackingElement();
		this.initVolumePricing();
		this.mivaEventsListener();
		this.initQuantify();
		this.initShortDesc();
		this.initTabs();
		this.initQNA();
		this.initTooltips();
		this.initSubscriptions();
		this.initWaitlists();
	}

	async initPurchaseForm () {
		const purchaseFormElement = document.getElementById('js-purchase-form');

		if (!purchaseFormElement) {
			return;
		}

		// Initialize ajax add to cart functionality
		if (purchaseFormElement.querySelector('.js-add-to-cart')) {
			const {default: AjaxAddToCart} = await import('extensions/ajax-add-to-cart');

			new AjaxAddToCart(purchaseFormElement, {
				scrollToMessage: 'error'
			});
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		new HTMLFormValidator({
			fieldContainer: '.g-product-layout-purchase__options-attribute',
			formElement: purchaseFormElement
		});

		this.initWishlists(purchaseFormElement);
	}

	async initWishlists (purchaseFormElement) {
		if (!purchaseFormElement) {
			return;
		}

		const controlElement = document.getElementsByClassName('js-add-to-wishlist-control')[0];

		if (!controlElement) {
			return;
		}

		const dialogElement = document.getElementById(controlElement.getAttribute('aria-controls'));

		if (!dialogElement) {
			return;
		}

		const {WishlistDialog} = await import('extensions/wishlists');
		const wishlistFormElement = document.getElementById('js-create-wishlist-form');

		const wishlistDialog = new WishlistDialog(dialogElement, controlElement, {
			toggleEnabled: Boolean(wishlistFormElement)
		});

		// Wishlist dialog login / registration functionality
		const loginRegisterElement = document.getElementById('js-wishlist-login-register');

		if (loginRegisterElement) {
			const {AjaxLoginRegister} = await import('extensions/ajax-login-register');

			new AjaxLoginRegister(loginRegisterElement, {
				// Add attribute selections or variant_id params to form action if present
				beforeSubmit: (ajaxLoginRegister, event) => {
					const formElement = event.target;
					const formElementActionUrl = new URL(formElement.action);
					const locationParams = new URLSearchParams(window.location.search);
					const variantIdParam = 'variant_id';

					if (locationParams.has(variantIdParam)) {
						formElementActionUrl.searchParams.set(variantIdParam, locationParams.get(variantIdParam));
					} else {
						const purchaseFormData = new FormData(purchaseFormElement);

						if (purchaseFormData.has('Product_Attribute_Count')) {
							const attributeParams = {};

							for (const fieldEntry of purchaseFormData.entries()) {
								if (fieldEntry[0].indexOf('Product_Attributes') !== 0) {
									continue;
								}

								const entryKeyParts = fieldEntry[0].split(':');

								if (entryKeyParts?.length !== 2) {
									continue;
								}

								if (!attributeParams.hasOwnProperty(entryKeyParts[0])) {
									attributeParams[entryKeyParts[0]] = {};
								}

								attributeParams[entryKeyParts[0]][entryKeyParts[1]] = fieldEntry[1];
							}

							for (const param in attributeParams) {
								if (!attributeParams[param].hasOwnProperty('code') || !attributeParams[param].hasOwnProperty('value') || attributeParams[param].value.toString() === '') {
									continue;
								}

								formElementActionUrl.searchParams.set(`${param}:code`, attributeParams[param].code);
								formElementActionUrl.searchParams.set(`${param}:value`, attributeParams[param].value);
							}
						}
					}

					formElement.action = formElementActionUrl.toString();
				}
			});
		}

		// Wishlist dialog add to wishlist and insert wishlist functionality
		if (wishlistFormElement) {
			const {AjaxAddToWishlist, AjaxInsertWishlist} = await import('extensions/wishlists');
			const {default: EventBus} = await import('@mvps-genesis/event-bus');
			const {MessageContainer, Message} = await import('extensions/messages');

			const messageContainerElement = purchaseFormElement.getElementsByClassName('js-add-to-cart-message')[0];
			let messageContainer = null;

			if (messageContainerElement) {
				messageContainer = new MessageContainer({
					container: messageContainerElement
				});
			}

			new AjaxInsertWishlist(wishlistFormElement);

			const ajaxAddToWishlist = new AjaxAddToWishlist(purchaseFormElement);
			const wishlistCountElements = document.getElementsByClassName('js-wishlists-count');

			/**
			 * Add new wishlist action element to action elements list on successful
			 * creation, and toggle display back to add to wishlist UI. Update elements
			 * containing wishlist counts with new wishlist count.
			 */
			EventBus.addEventListener('MVPS:insertWishlist', (event) => {
				const wishlist = event.detail?.data?.wishlist;

				if (!wishlist) {
					return;
				}

				const actionElement = ajaxAddToWishlist.createActionElement(wishlist);

				ajaxAddToWishlist.appendActionElement(actionElement);

				const autohideTime = ajaxAddToWishlist.messageOptions.success.options.autohideTime ?? 5000;

				ajaxAddToWishlist.createMessage(event.detail.message, {
					...ajaxAddToWishlist.messageOptions.success.options,
					autohide: true,
					autohideTime: autohideTime
				});

				setTimeout(() => {
					ajaxAddToWishlist.messageContainer.clear();
				}, autohideTime);

				wishlistDialog.toggle.elements.primary.controlElement.click();

				actionElement.focus();

				if (wishlistCountElements) {
					let wishlistsCount = parseInt(wishlistCountElements[0].textContent, 10);

					if (!isNaN(wishlistsCount)) {
						wishlistsCount += 1;

						for (const wishlistCountElement of wishlistCountElements) {
							wishlistCountElement.textContent = wishlistsCount;
						}
					}
				}
			});

			/**
			 * Hide wishlist dialog on success and display success message in
			 * purchase form message element
			 */
			EventBus.addEventListener('MVPS:addToWishlist', (event) => {
				const addToWishlistSuccess = event.detail?.success;

				if (!addToWishlistSuccess || !messageContainer) {
					return;
				}

				messageContainer.add(new Message(event.detail.message, {
					...ajaxAddToWishlist.messageOptions.success.options,
					autohide: true
				}));

				wishlistDialog.controlElement.click();
				ajaxAddToWishlist.messageContainer.clear();
			});
		}
	}

	async initStickyHeader () {
		const stickyHeaderElement = document.getElementById('js-sticky-header');
		const atcElement = document.getElementsByClassName('js-add-to-cart')[0];

		if (!stickyHeaderElement || !atcElement || !('IntersectionObserver' in window)) {
			return;
		}

		const {default: ProductStickyHeader} = await import('extensions/product-sticky-header');

		new ProductStickyHeader(stickyHeaderElement, {
			targetAddToCartElement: atcElement
		});
	}

	async initVolumePricing () {
		this.volumePricingElements = document.getElementsByClassName('js-volume-pricing');
		this.volumePricingElementsLen = this.volumePricingElements.length;

		if (!this.volumePricingElementsLen) {
			return;
		}

		const {AjaxVolumePricing} = await import('extensions/ajax-volume-pricing');

		for (let index = 0; index < this.volumePricingElementsLen; index++) {
			this.volumePricingElements[index].AjaxVolumePricing = new AjaxVolumePricing(this.volumePricingElements[index]);
		}
	}

	mivaEventsListener () {
		const observer = new MutationObserver((_mutations, mo) => {
			if (typeof window.MivaEvents !== 'undefined') {
				mo.disconnect();

				// eslint-disable-next-line new-cap
				window.MivaEvents.SubscribeToEvent('variant_changed', (productData) => this.variantChanged(productData));
			}
		});

		observer.observe(document, {
			childList: true,
			subtree: true
		});
	}

	variantChanged (productData) {
		this.updateVariantURL(productData.variant_id);
		this.updateVolumePricing(productData);
		this.waitlistShowHide(productData.variant?.inv_level !== 'out');
	}

	updateVariantURL (variantId) {

		let newSearch = '';

		if ((/variant_id=\d+/).test(window.location.search)) {
			newSearch = window.location.search.replace(/variant_id=\d+/, 'variant_id=' + variantId);
		} else if (window.location.search) {
			newSearch = window.location.search + '&variant_id=' + variantId;
		} else {
			newSearch = '?variant_id=' + variantId;
		}

		window.history.replaceState(null, '', window.location.pathname + newSearch + window.location.hash);

	}

	updateVolumePricing (productData) {
		if (!this.volumePricingElements || !this.volumePricingElementsLen) {
			return;
		}

		for (let index = 0; index < this.volumePricingElementsLen; index++) {
			if (!this.volumePricingElements[index].AjaxVolumePricing) {
				continue;
			}

			this.volumePricingElements[index].AjaxVolumePricing.updateContent(productData);
		}
	}

	async initQuantify () {
		const quantifyElement = document.querySelector('[data-hook="quantify"]');

		if (!quantifyElement) {
			return;
		}

		const {Quantify} = await import('extensions/quantify');

		new Quantify({
			wrapper: quantifyElement
		});
	}

	async initShortDesc () {
		const shortDescElement = document.getElementsByClassName('g-short-desc')[0];

		if (!shortDescElement) {
			return;
		}

		let maxWordCount = parseInt(shortDescElement.getAttribute('data-max-word-count'), 10);

		if (isNaN(maxWordCount)) {
			maxWordCount = 20;
		}

		const {default: ShortDesc} = await import('extensions/short-description');

		new ShortDesc({
			maxWordCount: maxWordCount,
			scrollToElement: false,
			shortDescElement: shortDescElement
		});
	}

	async initTabs () {
		const productTabs = document.getElementsByClassName('g-tabcordion');
		const productTabsLength = productTabs.length;

		if (!productTabsLength) {
			return;
		}

		const {Tabcordion} = await import('extensions/tabs');

		for (let i = 0; i < productTabsLength; i++) {
			productTabs[i].tabcordion = new Tabcordion(productTabs[i], {
				accordionMode: 768,
				hashOptions: {
					enabled: true,
					scrollIntoView: {
						behavior: 'smooth',
						block: 'start'
					},
					updateLocation: true
				}
			});

			if (i === 0) {
				const shortDescLinkElement = document.querySelector('.g-short-desc__link');

				if (shortDescLinkElement) {
					shortDescLinkElement.addEventListener('click', (event) => {
						const hashElements = productTabs[i].tabcordion.getElementsByHash(shortDescLinkElement.hash);

						if (hashElements) {
							event.preventDefault();

							productTabs[i].tabcordion.showPanel(hashElements, {
								scrollIntoView: productTabs[i].tabcordion.hashOptions.scrollIntoView
							});
						}
					});
				}
			}
		}
	}

	initSubscriptions () {
		const subTermElement = document.getElementById('js-subscription-terms');
		const subChoiceElements = document.getElementsByClassName('js-subscription-choice');

		if (!subTermElement || !subChoiceElements.length) {
			return;
		}

		const subtermOptionsElement = subTermElement.querySelector('[name="Product_Subscription_Term_ID"]');

		if (!subtermOptionsElement) {
			return;
		}

		const subTermControlElementId = 'js-subscription-term-control';

		for (const element of subChoiceElements) {
			element.addEventListener('click', () => {
				if (element.id === subTermControlElementId && element.checked) {
					subTermElement.removeAttribute('hidden');
					subtermOptionsElement.disabled = false;
				} else {
					subTermElement.setAttribute('hidden', '');
					subtermOptionsElement.disabled = true;
				}
			});
		}
	}
	
	// eslint-disable-next-line max-lines-per-function
	async initWaitlists () {
		const waitlistControlElements = document.getElementsByClassName('js-waitlist-control');

		if (!waitlistControlElements.length) {
			return;
		}

		const waitlistModalTriggerAttribute = 'data-waitlist-modal-trigger';
		const waitlistModalIds = [];

		for (const controlElement of waitlistControlElements) {
			const waitlistModalId = controlElement.getAttribute('data-waitlist-modal-trigger');

			if (!waitlistModalId) {
				continue;
			}

			waitlistModalIds.push(waitlistModalId);
		}

		if (!waitlistModalIds.length) {
			return;
		}

		const {default: Modal} = await import('extensions/modal');
		const modal = new Modal({triggerAttribute: waitlistModalTriggerAttribute});

		modal.init(waitlistModalIds);

		const waitlistFormClass = 'js-waitlist-form';
		const waitlistFormElements = document.getElementsByClassName(waitlistFormClass);

		if (!waitlistFormElements.length) {
			return;
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		for (const waitlistFormElement of waitlistFormElements) {
			new HTMLFormValidator({
				errorInsertPosition: 'afterend',
				fieldContainer: `.${waitlistFormClass}`,
				formElement: waitlistFormElement
			});

			waitlistFormElement.addEventListener('submit', async (event) => {
				const containerElement = waitlistFormElement.closest('.js-waitlist-content');
				const resultsElement = containerElement?.querySelector('.js-waitlist-results');

				if (!resultsElement) {
					return;
				}

				event.preventDefault();

				const requestUrl = waitlistFormElement.action ?? this.pageContext.Json_Url;
				const formData = new FormData(waitlistFormElement);

				const response = await fetch(requestUrl, {
					body: formData,
					method: waitlistFormElement.method ?? 'POST'
				});

				if (!response.ok) {
					this.handleWaitlistAddError(resultsElement);
				}

				try {
					const responseBody = await response.json();

					if (!responseBody.success) {
						this.handleWaitlistAddError(resultsElement);
					}
				} catch {
					this.handleWaitlistAddError(resultsElement);
				}

				waitlistFormElement.classList.add('u-hidden');
				resultsElement.classList.remove('u-hidden');
			});
		}
	}

	handleWaitlistAddError (resultsElement) {
		const resultsIconElement = resultsElement.querySelector('.g-add-to-waitlist-dialog__results-icon');

		resultsIconElement?.classList.add('u-hidden');

		const resultsTextElement = resultsElement.querySelector('.g-add-to-waitlist-dialog__results-text');

		if (resultsTextElement) {
			resultsTextElement.innerHTML = 'There was an issue signing up for the waitlist. Please try again later.';
		}
	}

	waitlistShowHide (hide = true) {
		const waitlistElem = document.getElementById('js-waitlist-container');
		if (!waitlistElem) {
			return;
		}

		if (hide) {
			waitlistElem.classList.add('u-hidden');
		} else {
			waitlistElem.classList.remove('u-hidden');
		}
	}
}
